import { Home, TrendingUp } from 'react-feather';

interface TopBrand {
  id: string;
  companyName: string;
  commission: number;
  status: 'Active' | 'Pending';
  joinedDate: string;
}

interface TopBrandsCardProps {
  brands: TopBrand[];
  title?: string;
}

export function TopBrandsCard({ brands, title = "My Brands" }: TopBrandsCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-card rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {title}
        </h3>
        <Home className="h-5 w-5 text-brand-600" />
      </div>

      <div className="space-y-4">
        {brands.length === 0 ? (
          <div className="text-center py-8">
            <Home className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-muted-foreground">No brands yet</p>
            <p className="text-sm text-muted-foreground">
              Start referring brands to see them here
            </p>
          </div>
        ) : (
          brands.map((brand) => (
            <div
              key={brand.id}
              className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                    <Home className="h-5 w-5 text-brand-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">
                      {brand.companyName}
                    </h4>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Joined {formatDate(brand.joinedDate)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="font-semibold text-foreground">
                  {formatCurrency(brand.commission)}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      brand.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {brand.status}
                  </span>
                  <TrendingUp className="h-3 w-3 text-green-600" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {brands.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Commission</span>
            <span className="font-semibold text-foreground">
              {formatCurrency(brands.reduce((sum, brand) => sum + brand.commission, 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
} 